import { Loader2, ClipboardList } from 'lucide-react';
import { TaskCard } from './TaskCard';
import type { Task } from '../../types';

interface TaskListProps {
  tasks: Task[];
  isLoading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  showPerson?: boolean;
  onEdit?: (task: Task) => void;
  onRetry?: () => void;
}

/**
 * Simple list of tasks (used on person detail page)
 */
export const TaskList = ({
  tasks,
  isLoading,
  error,
  emptyMessage = 'No tasks',
  showPerson = true,
  onEdit,
  onRetry,
}: TaskListProps) => {
  // Loading state
  if (isLoading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 text-sm text-blast-600 hover:text-blast-700 dark:text-blast-400 dark:hover:text-blast-400"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  // Empty state
  if (tasks.length === 0) {
    return (
      <div className="text-center py-8">
        <ClipboardList className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tasks.map((task) => (
        <TaskCard
          key={task.id}
          task={task}
          showPerson={showPerson}
          onEdit={onEdit}
        />
      ))}
    </div>
  );
};
